import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, Store } from "lucide-react";

import { cn } from "@/lib/cn";
import { BusinessOption } from "../types/webstoreAnalytics";

interface BusinessPickerProps {
  businessId: string;
  onChange: (businessId: string) => void;
  businesses: BusinessOption[];
  isLoading: boolean;
}

/**
 * Business filter with a search field.
 *
 * A native <select> cannot hold one, and the id is what people search by — they are
 * long, opaque and near-identical at a glance (`BIZ_a2fz228f684f19`), so scrolling a
 * plain list stops working the moment there is more than a handful of storefronts.
 */
export function BusinessPicker({
  businessId,
  onChange,
  businesses,
  isLoading,
}: BusinessPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery("");
  }, [open]);

  // Match the id and the name both — the id is what you paste in from a support
  // ticket, the name is what you remember.
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return businesses;
    return businesses.filter(
      (b) =>
        b.business_id.toLowerCase().includes(needle) ||
        b.business_name.toLowerCase().includes(needle)
    );
  }, [businesses, query]);

  const selected = businesses.find((b) => b.business_id === businessId);
  const buttonLabel = isLoading
    ? "Loading businesses…"
    : selected
      ? selected.business_id
      : "All businesses";

  const select = (nextId: string) => {
    onChange(nextId);
    setOpen(false);
  };

  return (
    <div className="relative min-w-[230px]" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "flex items-center gap-2 w-full bg-white border rounded-md px-2.5 py-1.5 text-xs transition-colors",
          open ? "border-accent-blue" : "border-slate-200 hover:bg-slate-50",
          isLoading ? "text-slate-400" : "text-slate-700"
        )}
      >
        <Store className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className={cn("flex-1 text-left truncate", selected && "num-mono")}>
          {buttonLabel}
        </span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 rounded-md border border-border bg-white shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search business ID or name"
              className="flex-1 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none"
            />
          </div>

          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            <Option
              label="All businesses"
              isSelected={businessId === "all"}
              onSelect={() => select("all")}
            />

            {filtered.map((business) => (
              <Option
                key={business.business_id}
                label={business.business_id}
                // The name is not what you search by, but it is what makes an id
                // recognisable. Hidden when it is just the id repeated back.
                hint={
                  business.business_name === business.business_id ? undefined : business.business_name
                }
                isSelected={business.business_id === businessId}
                onSelect={() => select(business.business_id)}
                mono
              />
            ))}

            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-[11px] text-slate-400">
                {businesses.length === 0
                  ? "No business had traffic in this range"
                  : `No business matches "${query}"`}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

interface OptionProps {
  label: string;
  hint?: string;
  isSelected: boolean;
  onSelect: () => void;
  mono?: boolean;
}

function Option({ label, hint, isSelected, onSelect, mono }: OptionProps) {
  return (
    <li role="option" aria-selected={isSelected}>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors",
          isSelected ? "bg-blue-50 text-ink-900" : "text-slate-700 hover:bg-slate-50"
        )}
      >
        <span className="flex-1 min-w-0">
          <span className={cn("block truncate", mono && "num-mono")}>{label}</span>
          {hint && <span className="block truncate text-[10px] text-slate-400 mt-0.5">{hint}</span>}
        </span>
        {isSelected && <Check className="h-3.5 w-3.5 text-accent-blue shrink-0" />}
      </button>
    </li>
  );
}
